<template>
  <!-- TopBar trigger — clicking opens the palette -->
  <button
    @click="openPalette"
    class="w-full flex items-center gap-2 px-3 h-9 rounded-lg text-sm transition-colors cursor-text"
    style="background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08);"
  >
    <MdiMagnify class="w-4 h-4 shrink-0 text-gray-500" />
    <span class="flex-1 text-left text-gray-500 text-sm">{{ searchPlaceholder }}</span>
  </button>

  <!-- Command Palette — Teleported to body so it's never clipped by the TopBar -->
  <Teleport to="body">
    <Transition name="palette">
      <div
        v-if="open"
        class="fixed inset-0 z-[200] flex items-start justify-center px-4"
        style="padding-top: min(15vh, 120px);"
      >
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="close" />

        <!-- Modal panel -->
        <div
          ref="modalPanel"
          class="relative w-full rounded-2xl shadow-2xl"
          style="max-width: 560px; background: #0d1520; border: 1px solid rgba(255,255,255,0.12); overflow: hidden;"
        >
          <!-- Input row -->
          <div class="flex items-center gap-3 px-4 py-3.5 border-b border-white/8">
            <MdiMagnify class="w-5 h-5 text-gray-500 shrink-0" />
            <input
              ref="paletteInput"
              v-model="query"
              :placeholder="$t('search.globalPlaceholder')"
              class="flex-1 bg-transparent text-white text-base outline-none placeholder-gray-600 min-w-0"
              @keydown.escape.stop="close"
              @keydown.arrow-down.prevent="navigateDown"
              @keydown.arrow-up.prevent="navigateUp"
              @keydown.enter.prevent="selectHighlighted"
            />
            <button
              v-if="query.length > 0"
              @click="query = ''"
              class="text-gray-500 hover:text-gray-300 transition-colors shrink-0"
            >
              <MdiClose class="w-4 h-4" />
            </button>
            <kbd
              v-else
              class="text-[11px] text-gray-600 border border-white/10 rounded px-1.5 py-0.5 font-mono shrink-0"
            >Esc</kbd>
          </div>

          <!-- Results -->
          <div ref="resultsContainer" class="overflow-y-auto" style="max-height: 58vh;">
            <!-- "Navigate to" section — always instant, never blocked by loading -->
            <div v-if="filteredNavItems.length > 0">
              <div class="px-4 pt-3 pb-1 text-xs font-semibold text-gray-500 tracking-wider uppercase">
              {{ $t('search.navigateTo') }}
              </div>
              <button
                v-for="(item, i) in filteredNavItems"
                :key="item.key"
                @click="navigateTo(item)"
                :data-result-index="i"
                class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-200 transition-colors"
                :class="selectedIndex === i ? 'bg-white/10' : 'hover:bg-white/5'"
              >
                <component :is="item.icon" class="w-4 h-4 shrink-0" :class="item.iconClass" />
                <span class="flex-1 text-left">{{ item.label }}</span>
                <span v-if="item.category" class="text-xs text-gray-600 capitalize shrink-0">
                  {{ item.category }}
                </span>
              </button>
            </div>

            <!-- Backend search results (query >= 2 chars) -->
            <div
              v-if="query.length >= 2"
              :class="{ 'border-t border-white/5 mt-1': filteredNavItems.length > 0 }"
            >
              <!-- Inline spinner — nav items above are already visible -->
              <div v-if="loading" class="flex items-center gap-2.5 px-4 py-3 text-sm text-gray-500">
                <div class="w-3.5 h-3.5 shrink-0 rounded-full border-2 border-gray-700 border-t-gray-400 animate-spin" />
                {{ $t('search.searching') }}
              </div>
              <template v-else>
                <ResultGroup
                  v-for="group in sortedGroups"
                  :key="group.key"
                  :label="group.label"
                  :icon="group.icon"
                  :iconClass="group.iconClass"
                  :items="group.items"
                  :route="group.route"
                  :useId="group.useId"
                  :offset="group.offset"
                  :selectedIndex="selectedIndex"
                />
                <div v-if="!hasResults && filteredNavItems.length === 0" class="py-6 text-center text-sm text-gray-400">
                  {{ $t('search.noResultsFor', { query }) }}
                </div>
              </template>
            </div>

            <!-- Empty hint when palette first opens -->
            <div
              v-else-if="query.length === 0 && filteredNavItems.length === 0"
              class="py-6 text-center text-sm text-gray-500"
            >
              {{ $t('search.startTyping') }}
            </div>
          </div>

          <!-- Footer keyboard hints -->
          <div class="px-4 py-2.5 border-t border-white/5 flex items-center gap-4 text-[11px] text-gray-600">
            <span>
              <kbd class="font-mono border border-white/10 rounded px-1 py-0.5">↑</kbd>
              <kbd class="font-mono border border-white/10 rounded px-1 py-0.5 ml-0.5">↓</kbd>
              {{ $t('search.navigateHint') }}
            </span>
            <span><kbd class="font-mono border border-white/10 rounded px-1 py-0.5">↵</kbd> {{ $t('search.openHint') }}</span>
            <span><kbd class="font-mono border border-white/10 rounded px-1 py-0.5">Esc</kbd> {{ $t('search.closeHint') }}</span>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount, defineComponent, h } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/stores/store.js';
import { usePermissions } from '@/composables/usePermissions.js';
import { getSearchResults } from '@/api/api.js';
import { NAV_ITEMS } from '@/config/navItems.js';
import MdiMagnify from '~icons/mdi/magnify';
import MdiClose from '~icons/mdi/close';
import MdiBriefcase from '~icons/mdi/briefcase';
import MdiTag from '~icons/mdi/tag';
import MdiFolder from '~icons/mdi/folder';
import MdiAccountGroup from '~icons/mdi/account-group';
import MdiFileDocument from '~icons/mdi/file-document';
import MdiTagMultiple from '~icons/mdi/tag-multiple';
import MdiMap from '~icons/mdi/map';
import MdiMapMarker from '~icons/mdi/map-marker';
import MdiWarehouse from '~icons/mdi/warehouse';
import MdiPackageVariant from '~icons/mdi/package-variant';
import MdiBell from '~icons/mdi/bell';
import MdiClipboardCheck from '~icons/mdi/clipboard-check';
import MdiSwapHorizontal from '~icons/mdi/swap-horizontal';

const router = useRouter();
const route  = useRoute();
const store  = useStore();
const perms  = usePermissions();
const { t } = useI18n();

const open             = ref(false);
const query            = ref('');
const loading          = ref(false);
const selectedIndex    = ref(-1);
const paletteInput     = ref(null);
const resultsContainer = ref(null);

const isMac = computed(() => /(Mac|iPhone|iPod|iPad)/i.test(navigator.userAgent));
const searchPlaceholder = computed(() => isMac.value ? t('ui.kToSearch') : t('ui.ctrlKToSearch'));

const emptyResults = () => ({
  item: [], item_metadata: [], project: [], customer: [],
  wiki: [], category: [], area: [], location: [], warehouse: [], bundle: [],
});

const results = ref(emptyResults());

const hasResults = computed(() =>
  Object.values(results.value).some(arr => arr && arr.length > 0)
);

// ── Quick actions — palette-only verbs for warehouse ops ─────────────────────
const QUICK_ACTIONS = [
  { key: 'action-return', labelKey: 'ux.missionReturn', to: '/return', icon: MdiClipboardCheck, iconClass: 'text-amber-300', category: 'action' },
  { key: 'action-checkout', labelKey: 'ux.missionCheckout', to: '/checkout/', icon: MdiClipboardCheck, iconClass: 'text-emerald-400', category: 'action' },
  { key: 'action-transfers', labelKey: 'ux.missionTransfers', to: '/transfers', icon: MdiSwapHorizontal, iconClass: 'text-purple-400', category: 'action' },
  { key: 'action-projects', labelKey: 'ux.missionPick', to: '/projects', icon: MdiFolder, iconClass: 'text-indigo-400', category: 'action' },
  { key: 'notification-preferences', labelKey: 'profile.notificationPreferences', to: '/profile/notifications', icon: MdiBell, iconClass: 'text-blue-400', category: 'settings' },
];

// ── "Navigate to" — client-side nav item fuzzy match ─────────────────────────
const filteredNavItems = computed(() => {
  const q = query.value.toLowerCase().trim();

  const navItems = NAV_ITEMS.filter(item => {
    if (!item.inSideMenu) return false;
    if (item.requiredPermission && !(perms[item.requiredPermission]?.value ?? false)) return false;
    if (!q) return true;
    return (
      t(item.labelKey).toLowerCase().includes(q) ||
      (item.category && item.category.includes(q))
    );
  }).map(item => ({ ...item, label: t(item.labelKey) }));

  const quickItems = QUICK_ACTIONS.map(item => ({ ...item, label: t(item.labelKey) })).filter(item =>
    !q || item.label.toLowerCase().includes(q) || (item.category && item.category.includes(q))
  );

  const combined = [...navItems, ...quickItems];
  // When no query, show a concise quick-access set; when typing, show all matches
  return q ? combined : combined.slice(0, 8);
});

// ── Backend search groups ─────────────────────────────────────────────────────
const ALL_GROUPS = [
  { key: 'item',          labelKey: 'bulkEdit.resources.items',      icon: MdiBriefcase,      iconClass: 'text-blue-400',    route: '/item/',      useId: true },
  { key: 'item_metadata', labelKey: 'ui.assetTags', icon: MdiTag,            iconClass: 'text-cyan-400',    route: '/instance/',  useId: true },
  { key: 'project',       labelKey: 'entity.project_plural',   icon: MdiFolder,         iconClass: 'text-indigo-400',  route: '/project/',   useId: true },
  { key: 'customer',      labelKey: 'entity.customer_plural',  icon: MdiAccountGroup,   iconClass: 'text-sky-400',     route: '/customers/', useId: true },
  { key: 'bundle',        labelKey: 'entity.bundle_plural',    icon: MdiPackageVariant,  iconClass: 'text-emerald-400', route: '/bundles/',   useId: true },
  { key: 'wiki',          labelKey: 'nav.wiki',       icon: MdiFileDocument,   iconClass: 'text-slate-300',   route: '/wiki/',      useId: true },
  { key: 'category',      labelKey: 'entity.category_plural', icon: MdiTagMultiple,    iconClass: 'text-violet-400',  route: '/category/',  useId: true },
  { key: 'area',          labelKey: 'bulkEdit.resources.areas',      icon: MdiMap,            iconClass: 'text-teal-400',    route: '/area/',      useId: true },
  { key: 'location',      labelKey: 'bulkEdit.resources.locations',  icon: MdiMapMarker,      iconClass: 'text-orange-400',  route: '/location/',  useId: true },
  { key: 'warehouse',     labelKey: 'entity.warehouse_plural', icon: MdiWarehouse,      iconClass: 'text-amber-400',   route: '/warehouse/', useId: true },
];

// Groups ordered by similarity score; offsets account for nav items rendered above them
const sortedGroups = computed(() => {
  const navOffset = filteredNavItems.value.length;
  const populated = ALL_GROUPS.map(group => ({ ...group, label: t(group.labelKey) }))
    .map(g => ({ ...g, items: results.value[g.key] || [] }))
    .filter(g => g.items.length > 0)
    .sort((a, b) => (b.items[0]?.similarity ?? 0) - (a.items[0]?.similarity ?? 0));

  let offset = navOffset;
  return populated.map(g => {
    const entry = { ...g, offset };
    offset += Math.min(g.items.length, 5);
    return entry;
  });
});

// Unified flat list for keyboard navigation (nav items first, then backend)
const allFlatResults = computed(() => {
  const nav = filteredNavItems.value.map(item => ({ type: 'nav', item }));
  const backend = [];
  sortedGroups.value.forEach(group => {
    group.items.slice(0, 5).forEach(item => {
      backend.push({ type: 'backend', item, group });
    });
  });
  return [...nav, ...backend];
});

// ── Data fetching ─────────────────────────────────────────────────────────────
let debounce = null;
watch(query, (val) => {
  clearTimeout(debounce);
  // Nav items are a synchronous computed — auto-select the first hit immediately
  selectedIndex.value = filteredNavItems.value.length > 0 ? 0 : -1;
  if (val.length < 2) {
    loading.value = false;
    results.value = emptyResults();
    return;
  }
  loading.value = true;
  debounce = setTimeout(async () => {
    try {
      const res = await getSearchResults(val);
      const raw = res.results ?? {};
      const sorted = {};
      for (const [key, arr] of Object.entries(raw)) {
        sorted[key] = [...arr].sort((a, b) => (b.similarity ?? 0) - (a.similarity ?? 0));
      }
      results.value = { ...emptyResults(), ...sorted };
      // If no nav item was selected, jump to the top API result
      if (selectedIndex.value === -1 && allFlatResults.value.length > 0) {
        selectedIndex.value = 0;
      }
    } catch (_) {
      // silently swallow search errors
    } finally {
      loading.value = false;
    }
  }, 200);
});

// Close palette on route change (e.g. browser back/forward)
watch(() => route.path, () => {
  if (open.value) close();
});

// ── Open / close ──────────────────────────────────────────────────────────────
async function openPalette() {
  open.value = true;
  await nextTick();
  paletteInput.value?.focus();
}

function close() {
  clearTimeout(debounce);
  debounce = null;
  open.value = false;
  query.value = '';
  loading.value = false;
  results.value = emptyResults();
  selectedIndex.value = -1;
}

// ── Keyboard navigation ───────────────────────────────────────────────────────
function navigateDown() {
  if (!open.value || allFlatResults.value.length === 0) return;
  selectedIndex.value = (selectedIndex.value + 1) % allFlatResults.value.length;
  scrollToSelected();
}

function navigateUp() {
  if (!open.value || allFlatResults.value.length === 0) return;
  selectedIndex.value = selectedIndex.value <= 0
    ? allFlatResults.value.length - 1
    : selectedIndex.value - 1;
  scrollToSelected();
}

function selectHighlighted() {
  const selected = allFlatResults.value[selectedIndex.value];
  if (!selected) return;
  if (selected.type === 'nav') {
    navigateTo(selected.item);
  } else {
    handleNavigate(selected.item, selected.group.label, selected.group.route, selected.group.useId);
  }
}

function scrollToSelected() {
  setTimeout(() => {
    const el = resultsContainer.value?.querySelector(`[data-result-index="${selectedIndex.value}"]`);
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, 0);
}

// Global Cmd/Ctrl+K — toggle palette
function handleGlobalKeydown(e) {
  if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
    e.preventDefault();
    open.value ? close() : openPalette();
  }
}

onMounted(() => window.addEventListener('keydown', handleGlobalKeydown));
onBeforeUnmount(() => {
  clearTimeout(debounce);
  window.removeEventListener('keydown', handleGlobalKeydown);
});

// ── Navigation actions ────────────────────────────────────────────────────────
function navigateTo(item) {
  router.push(item.to);
  close();
}

const handleNavigate = (item, label, itemRoute, useId) => {
  if (itemRoute === '/warehouse/' && item) {
    store.setCurrentWarehouse({ id: item.object_id, name: item.display?.name });
    router.push('/');
    close();
    return;
  }
  if (itemRoute === '/location/' && item) {
    store.setCurrentLocation({ id: item.object_id, name: item.display?.name });
    router.push('/');
    close();
    return;
  }
  if (itemRoute === '/category/' && item.object_id) {
    router.push(`/items?category=${item.object_id}`);
    close();
    return;
  }
  if (itemRoute === '/area/' && item.display?.name) {
    router.push(`/items?area=${encodeURIComponent(item.display.name)}`);
    close();
    return;
  }
  const target = useId && item.object_id != null ? `${itemRoute}${item.object_id}` : itemRoute;
  if (target) {
    router.push(target);
    close();
  }
};

// ── ResultGroup inline component ──────────────────────────────────────────────
const ResultGroup = defineComponent({
  props: {
    label:         String,
    icon:          Object,
    iconClass:     { type: String, default: 'text-gray-400' },
    items:         { type: Array, default: () => [] },
    route:         String,
    useId:         { type: Boolean, default: true },
    offset:        { type: Number, default: 0 },
    selectedIndex: { type: Number, default: -1 },
  },
  setup(props) {
    const navigate     = (item) => handleNavigate(item, props.label, props.route, props.useId);
    const itemLabel    = (item) => item?.display?.name || 'Unnamed';
    const itemSub      = (item) => item?.display?.subtitle || null;
    const getGlobalIdx = (i) => props.offset + i;

    return () => {
      const validItems = (props.items || []).filter(item => item?.object_id != null);
      if (!validItems.length) return null;

      return h('div', { class: 'border-b border-white/5 last:border-0' }, [
        h('div', {
          class: 'px-4 pt-3 pb-1 text-xs font-semibold text-gray-500 tracking-wider uppercase flex items-center gap-2',
        }, [
          h(props.icon, { class: ['w-3.5 h-3.5', props.iconClass].join(' ') }),
          props.label,
        ]),
        ...validItems.slice(0, 5).map((item, i) => {
          const globalIndex = getGlobalIdx(i);
          const isSelected  = globalIndex === props.selectedIndex;
          const sub = itemSub(item);
          return h('button', {
            key: item.object_id,
            onClick: () => navigate(item),
            'data-result-index': globalIndex,
            class: [
              'w-full text-left px-4 py-2.5 text-sm text-gray-200 transition-colors flex items-center gap-2',
              isSelected ? 'bg-white/10' : 'hover:bg-white/5',
            ].join(' '),
          }, [
            h('span', { class: 'truncate flex-1' }, itemLabel(item)),
            sub ? h('span', { class: 'text-xs text-gray-500 shrink-0 truncate max-w-[8rem]' }, sub) : null,
          ]);
        }),
      ]);
    };
  },
});
</script>

<style scoped>
.palette-enter-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.palette-leave-active { transition: opacity 0.1s ease, transform 0.1s ease; }
.palette-enter-from,
.palette-leave-to     { opacity: 0; transform: scale(0.97) translateY(-8px); }
</style>
